import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { extractErrorMessage } from "@/lib/errors";
import {
  addApiToken,
  createApiKey,
  createUserAllocation,
  deactivateApiKey,
  deleteApiToken,
  deleteUserAllocation,
  getApiToken,
  getSubscriptionOverview,
  getTeamActivityLogs,
  getTeamMembers,
  getUserAllocations,
  saveUserAllocations,
} from "./token.api";
import { useNavigate } from "@tanstack/react-router";
import type {
  IUserAllocationResponse,
  TeamActivityLogParams,
} from "../types/token.types";

// API Token Queries
export const useGetApiToken = (
  page: number = 1,
  pageSize: number = 10,
  activeOnly: boolean = true,
) => {
  return useQuery({
    queryKey: ["api-token", page, pageSize, activeOnly],
    queryFn: () =>
      getApiToken({ page, page_size: pageSize, active_only: activeOnly }),
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 60 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true, // OVERRIDE GLOBAL DEFAULT
  });
};

export const useAddApiToken = (entity_type: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => addApiToken({ entity_type }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api-token"] });
      toast.success("Token created successfully");
    },
    onError: (error: Error) => {
      toast.error("Failed to create token", {
        description: extractErrorMessage(error),
      });
    },
  });
};

export const useDeleteApiToken = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      user_uuid,
      token_id,
    }: {
      user_uuid: string;
      token_id: string;
    }) => deleteApiToken({ user_uuid, token_id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api-token"] });
      toast.success("Token deleted successfully");
    },
    onError: (error: Error) => {
      toast.error("Failed to delete token", {
        description: extractErrorMessage(error),
      });
    },
  });
};

export const useGetSubscriptionOverview = () => {
  return useQuery({
    queryKey: ["subscription-overview"],
    queryFn: () => getSubscriptionOverview(),
  });
};

export const useGetUserAllocations = (userId: string) => {
  return useQuery({
    queryKey: ["user-allocations", userId],
    queryFn: () => getUserAllocations(userId),
    enabled: !!userId,
  });
};

// Hook to fetch allocations for multiple users
export const useGetMultipleUserAllocations = (userIds: string[]) => {
  return useQuery({
    queryKey: ["multiple-user-allocations", userIds],
    queryFn: async () => {
      const promises = userIds.map((userId) => getUserAllocations(userId));
      const results = await Promise.all(promises);
      return results.reduce(
        (acc, result) => {
          acc[result.user_id] = result;
          return acc;
        },
        {} as Record<string, IUserAllocationResponse>,
      );
    },
    enabled: userIds.length > 0,
  });
};

// Hook to save user allocations
export const useSaveUserAllocations = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      allocations,
    }: {
      userId: string;
      allocations: {
        allocation_id: string;
        allocated_quota: number;
        allocated_rate_limit: number;
        enabled: boolean;
      }[];
    }) => saveUserAllocations(userId, allocations),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["user-allocations"] });
      queryClient.invalidateQueries({
        queryKey: ["multiple-user-allocations"],
      });
      queryClient.invalidateQueries({ queryKey: ["token-overview"] });
      toast.success(data.message || "Token allocations saved successfully");
    },
    onError: (error: Error) => {
      toast.error("Failed to save token allocations", {
        description: extractErrorMessage(error),
      });
    },
  });
};

// Hook to create user allocation
export const useCreateUserAllocation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      parent_token_id,
      entity_type,
    }: {
      userId: string;
      parent_token_id: string;
      entity_type: string;
    }) => createUserAllocation({ userId, parent_token_id, entity_type }),

    // Optimistic update
    onMutate: async ({ userId, parent_token_id, entity_type }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["token-overview"] });

      // Snapshot previous value
      const previousData = queryClient.getQueryData(["token-overview"]);

      // Optimistically update
      queryClient.setQueryData(["token-overview"], (old: any) => {
        if (!old) return old;

        const updatedTeamMembers = old.team_members?.map((member: any) => {
          if (member.user_id === userId) {
            // Add temporary allocation
            const tempAllocation = {
              allocation_id: `temp-${Date.now()}`,
              token_name: `New ${entity_type} Token`,
              entity_type,
              parent_token_id,
              allocated_quota: 0,
              allocated_rate_limit: 0,
              enabled: false,
              quota_remaining: 0,
              max_quota_available: 100000,
              max_rate_limit_available: 5000,
              isOptimistic: true, // Flag for UI handling
            };

            return {
              ...member,
              allocations: [...(member.allocations || []), tempAllocation],
            };
          }
          return member;
        });

        return {
          ...old,
          team_members: updatedTeamMembers || old.team_members,
        };
      });

      return { previousData };
    },

    onSuccess: () => {
      // Invalidate and refetch to get real data
      queryClient.invalidateQueries({ queryKey: ["token-overview"] });
      queryClient.invalidateQueries({ queryKey: ["api-token"] });
      toast.success("Token allocation created successfully");
    },

    onError: (error: Error, _variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(["token-overview"], context.previousData);
      }

      toast.error("Failed to create token allocation", {
        description: extractErrorMessage(error),
      });
    },
  });
};

// Hook to delete user allocation
export const useDeleteUserAllocation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      allocationId,
    }: {
      userId: string;
      allocationId: string;
    }) => deleteUserAllocation({ userId, allocationId }),
    onSuccess: (data) => {
      // Invalidate and refetch relevant queries
      queryClient.invalidateQueries({ queryKey: ["token-overview"] });

      toast.success(data.message || "Token allocation deleted successfully");
    },
    onError: (error: Error) => {
      toast.error("Failed to delete token allocation", {
        description: extractErrorMessage(error),
      });
    },
  });
};

// Legacy queries (will be removed)
export const useGetTeamMembers = () => {
  return useQuery({
    queryKey: ["team-members"],
    queryFn: getTeamMembers,
  });
};

// Team Activity Logs
export const useGetTeamActivityLogs = (params: TeamActivityLogParams = {}) => {
  return useQuery({
    queryKey: ["team-activity-logs", params],
    queryFn: () => getTeamActivityLogs(params),
  });
};

// Deactivate an API key by ID (manager action, different from deleteApiToken)
export const useDeactivateApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (apiKeyId: string) => deactivateApiKey(apiKeyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api-token"] });
      toast.success("Token deactivated successfully");
    },
    onError: (error: Error) => {
      toast.error("Failed to deactivate token", {
        description: extractErrorMessage(error),
      });
    },
  });
};

// Create API Key for a subscription plan
export const useCreateApiKey = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (planId: string) => createApiKey(planId),
    retry: 0,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api-token"] });
      toast.success("API key created successfully");
      navigate({ to: "/manager/subscription-management/usage" });
    },
    onError: (error: Error) => {
      toast.error("Failed to create API key", {
        description: extractErrorMessage(error),
      });
    },
  });
};
